import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Box,
	Button,
	Card,
	CardContent,
	FormControl,
	FormControlLabel,
	FormLabel,
	IconButton,
	Radio,
	RadioGroup,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import type React from "react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { apiClient, type CreateInformation } from "@/shared/lib/api/api-client";
import { informationSchema } from "@/shared/schemas/informationSchema";
import { convertDateTimeLocalToApi, formatDate, formatDateTimeForApi } from "@/shared/utils";
import { useImageUpload } from "../hooks";
import InformationCreateConfirmDialog from "./InformationCreateConfirmDialog";

interface CreateInformationFormProps {
	initialData?: {
		title: string;
		body: string;
	};
	onSuccess?: (message?: string) => void;
	onError?: (message: string) => void;
}

export default function CreateInformationForm({
	initialData,
	onSuccess,
	onError,
}: CreateInformationFormProps): React.ReactElement {
	const navigate = useNavigate();
	const bodyInputRef = useRef<HTMLTextAreaElement>(null);
	const { uploadedImages, handleImageUpload, removeUploadedImage } = useImageUpload();
	const [formData, setFormData] = useState<CreateInformation>({
		title: initialData?.title || "",
		body: initialData?.body || "",
		filenameList: [],
	});
	const [loading, setLoading] = useState(false);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [errors, setErrors] = useState<{ title?: string; body?: string }>({});

	// 予約配信関連の状態
	const [deliveryType, setDeliveryType] = useState<"immediate" | "scheduled">("immediate");
	const [scheduledDateTime, setScheduledDateTime] = useState("");

	const handleChange =
		(field: keyof CreateInformation) =>
		(
			event:
				| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
				| { target: { value: string } },
		) => {
			const value = event.target.value;
			setFormData({
				...formData,
				[field]: value,
			});

			// リアルタイムバリデーション
			if (field === "title" || field === "body") {
				const result = informationSchema.shape[field].safeParse(value);
				if (!result.success) {
					setErrors((prev) => ({
						...prev,
						[field]: result.error.issues[0]?.message,
					}));
				} else {
					setErrors((prev) => {
						const newErrors = { ...prev };
						delete newErrors[field];
						return newErrors;
					});
				}
			}
		};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		// バリデーション
		const validationResult = informationSchema.safeParse({
			title: formData.title,
			body: formData.body,
		});

		if (!validationResult.success) {
			const fieldErrors: { title?: string; body?: string } = {};
			for (const issue of validationResult.error.issues) {
				const field = issue.path[0] as "title" | "body";
				if (!fieldErrors[field]) {
					fieldErrors[field] = issue.message;
				}
			}
			setErrors(fieldErrors);
			return;
		}

		// 予約配信の場合は日時が必須
		if (deliveryType === "scheduled" && !scheduledDateTime) {
			onError?.("予約配信を選択した場合、配信日時を指定してください");
			return;
		}

		// 予約配信の日時が過去でないかチェック
		if (deliveryType === "scheduled" && scheduledDateTime) {
			const scheduledDate = new Date(scheduledDateTime);
			const now = new Date();
			if (scheduledDate <= now) {
				onError?.("配信日時は現在時刻より後の時間を指定してください");
				return;
			}
		}

		setConfirmDialogOpen(true);
	};

	const handleConfirm = async () => {
		setConfirmDialogOpen(false);
		setLoading(true);

		try {
			// ファイル名のリストを生成（拡張子を含む）
			const filenameList = uploadedImages.map((img) => img.file.name);

			// プレースホルダーをファイル名に変換したbodyを作成
			let processedBody = formData.body;
			for (const img of uploadedImages) {
				processedBody = processedBody.replace(
					`{{image-${img.id}}}`,
					`{{filename:${img.file.name}}}`,
				);
			}

			const dataToSubmit = {
				...formData,
				body: processedBody,
				filenameList,
				date:
					deliveryType === "scheduled" && scheduledDateTime
						? convertDateTimeLocalToApi(scheduledDateTime)
						: formatDateTimeForApi(new Date()), // 即時配信の場合は現在時刻
			};

			// バックエンドにお知らせを作成し、署名付きURLを取得
			const result = await apiClient.information.create(dataToSubmit);

			if (result.error) {
				onError?.(result.error);
				return;
			}

			// 署名付きURLを使って画像をアップロード
			const presignedUrls =
				(result.data as any)?.presignedUrlToPutInformationImage ||
				result.data?.presignedUrlToPutInformationImageList ||
				[];
			if (presignedUrls.length > 0) {
				for (let i = 0; i < uploadedImages.length; i++) {
					const { file } = uploadedImages[i];
					// オブジェクト形式とstring形式の両方に対応
					const presignedUrl =
						typeof presignedUrls[i] === "string" ? presignedUrls[i] : presignedUrls[i]?.signedUrl;
					if (presignedUrl) {
						try {
							const uploadResponse = await fetch(presignedUrl, {
								method: "PUT",
								body: file,
							});

							if (!uploadResponse.ok) {
								throw new Error(`Failed to upload ${file.name}`);
							}
						} catch (_error) {
							onError?.(`画像 ${file.name} のアップロードに失敗しました`);
							return;
						}
					}
				}
			}

			const successMessage =
				deliveryType === "scheduled" ? "お知らせを予約しました" : "お知らせを配信しました";
			onSuccess?.(successMessage);
			setTimeout(() => {
				navigate("/informations");
			}, 3000);
		} catch (error) {
			onError?.(error instanceof Error ? error.message : "エラーが発生しました");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		navigate("/informations");
	};

	const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;

		const imgTags = handleImageUpload(files);

		// カーソル位置にimgタグを挿入
		if (bodyInputRef.current) {
			const cursorPosition = bodyInputRef.current.selectionStart || formData.body.length;
			const textBefore = formData.body.substring(0, cursorPosition);
			const textAfter = formData.body.substring(cursorPosition);

			const newBody = textBefore + imgTags + textAfter;

			setFormData({
				...formData,
				body: newBody,
			});

			// フォーカスを戻す
			setTimeout(() => {
				if (bodyInputRef.current) {
					const newCursorPosition = cursorPosition + imgTags.length;
					bodyInputRef.current.focus();
					bodyInputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
				}
			}, 0);
		}

		// inputをリセット
		e.target.value = "";
	};

	return (
		<Box
			sx={{
				display: "flex",
				justifyContent: "center",
				alignItems: "flex-start",
				width: "100%",
			}}
		>
			<Card
				sx={{
					width: "100%",
					maxWidth: 900,
					boxShadow: 3,
					alignSelf: "center",
				}}
			>
				<CardContent sx={{ p: 4 }}>
					<form onSubmit={handleSubmit}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
							<TextField
								label="タイトル"
								required
								fullWidth
								value={formData.title}
								onChange={handleChange("title")}
								placeholder="お知らせのタイトルを入力してください"
								error={!!errors.title}
								helperText={errors.title}
							/>

							{/* 本文入力 */}
							<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
								<Tooltip title="画像を追加" placement="left">
									<IconButton component="label" color="primary" size="medium">
										<AttachFileIcon />
										<input
											type="file"
											hidden
											accept="image/*"
											multiple
											onChange={handleImageSelect}
										/>
									</IconButton>
								</Tooltip>
								<TextField
									label="本文HTML"
									required
									fullWidth
									multiline
									rows={6}
									value={formData.body}
									onChange={handleChange("body")}
									placeholder="お知らせの内容を入力してください"
									inputRef={bodyInputRef}
									error={!!errors.body}
									helperText={errors.body || `${formData.body.length}/20000 文字`}
								/>
							</Box>

							{/* 配信設定 */}
							<Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}>
								<FormControl component="fieldset">
									<FormLabel component="legend" sx={{ mb: 1, fontWeight: "bold" }}>
										配信設定
									</FormLabel>
									<RadioGroup
										value={deliveryType}
										onChange={(e) => setDeliveryType(e.target.value as "immediate" | "scheduled")}
									>
										<FormControlLabel
											value="immediate"
											control={<Radio />}
											label="即時配信"
											sx={{ mb: 1 }}
										/>
										<FormControlLabel
											value="scheduled"
											control={<Radio />}
											label="予約配信"
											sx={{ mb: 1 }}
										/>
									</RadioGroup>

									{deliveryType === "scheduled" && (
										<Box sx={{ mt: 2 }}>
											<TextField
												label="配信日時"
												type="datetime-local"
												value={scheduledDateTime}
												onChange={(e) => setScheduledDateTime(e.target.value)}
												fullWidth
												required
												InputLabelProps={{
													shrink: true,
												}}
												inputProps={{
													min: new Date().toISOString().slice(0, 16),
												}}
												helperText="指定した日時に自動で配信されます"
											/>
										</Box>
									)}
								</FormControl>
							</Box>

							{/* 画像アップロード */}
							<Box>
								<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
									アップロード済み画像
								</Typography>

								{/* 画像プレビュー */}
								{uploadedImages.length > 0 ? (
									<Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
										{uploadedImages.map((img) => (
											<Box
												key={img.id}
												sx={{
													position: "relative",
													width: 120,
													height: 120,
													borderRadius: 1,
													overflow: "hidden",
													border: "1px solid #e0e0e0",
												}}
											>
												<img
													src={img.preview}
													alt={`upload-${img.id}`}
													style={{
														width: "100%",
														height: "100%",
														objectFit: "cover",
													}}
												/>
												<IconButton
													size="small"
													sx={{
														position: "absolute",
														top: 4,
														right: 4,
														backgroundColor: "rgba(255, 255, 255, 0.9)",
														"&:hover": {
															backgroundColor: "rgba(255, 255, 255, 1)",
														},
													}}
													onClick={() => {
														removeUploadedImage(img.id);

														// 本文から該当する画像タグを削除
														const updatedBody = formData.body.replace(
															`<img src="{{image-${img.id}}}" />`,
															"",
														);
														setFormData({
															...formData,
															body: updatedBody,
														});
													}}
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Box>
										))}
									</Box>
								) : (
									<Typography variant="body2" color="text.secondary">
										画像が選択されていません
									</Typography>
								)}
							</Box>

							{/* プレビュー */}
							<Box>
								<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
									プレビュー
								</Typography>
								<Box
									sx={{
										backgroundColor: "#ff5f49",
										p: 3,
										borderRadius: 2,
										display: "flex",
										justifyContent: "center",
									}}
								>
									<Card
										sx={{
											backgroundColor: "#FFFFFF",
											color: "#000000",
											boxShadow: 2,
											borderRadius: 3,
											width: "100%",
											maxWidth: 500,
										}}
									>
										<CardContent sx={{ p: 0 }}>
											<Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
												{/* 日付 */}
												<Typography
													sx={{
														color: "#8c8c8c",
														fontSize: "12px",
														mb: 0.5,
														pl: 1,
													}}
												>
													{formatDate(new Date())}
												</Typography>

												{/* タイトル */}
												<Typography
													sx={{
														fontSize: "18px",
														fontWeight: "bold",
														mb: 2,
														pl: 1,
														color: "#ff5f49",
													}}
												>
													{formData.title || "タイトル"}
												</Typography>

												{/* ボーダー */}
												<Box
													sx={{
														height: "1px",
														backgroundColor: "#e0e0e0",
														mb: 3,
													}}
												/>

												{/* 本文 */}
												<Box
													sx={{
														pl: 1,
														"& p": {
															margin: 0,
															marginBottom: "1em",
															fontWeight: "medium",
															wordBreak: "break-word",
															overflowWrap: "break-word",
														},
														"& a": {
															textDecoration: "underline",
															color: "#000000",
														},
														"& img": {
															width: "80%",
															display: "block",
															margin: "auto",
														},
														"& ul": {
															paddingLeft: "1em",
															"& li": {
																paddingLeft: "1em",
																listStyle: "none",
																position: "relative",
																"&::before": {
																	content: '"・"',
																	marginLeft: "-1em",
																	position: "absolute",
																	left: 0,
																},
															},
														},
													}}
												>
													<Box
														sx={{
															fontSize: "14px",
															lineHeight: 1.8,
															wordBreak: "break-word",
															overflowWrap: "break-word",
														}}
														dangerouslySetInnerHTML={{
															__html: (() => {
																let body = formData.body || "本文HTML";
																for (const img of uploadedImages) {
																	body = body.replace(`{{image-${img.id}}}`, img.preview);
																}
																return body;
															})(),
														}}
													/>
												</Box>
											</Box>
										</CardContent>
									</Card>
								</Box>
							</Box>

							<Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
								<Button
									variant="outlined"
									onClick={handleCancel}
									disabled={loading}
									sx={{ minWidth: 120 }}
								>
									キャンセル
								</Button>
								<Button type="submit" variant="contained" disabled={loading} sx={{ minWidth: 120 }}>
									{loading
										? deliveryType === "scheduled"
											? "予約中..."
											: "配信中..."
										: deliveryType === "scheduled"
											? "予約"
											: "配信"}
								</Button>
							</Box>
						</Box>
					</form>
				</CardContent>
			</Card>

			<InformationCreateConfirmDialog
				open={confirmDialogOpen}
				onConfirm={handleConfirm}
				onCancel={() => setConfirmDialogOpen(false)}
				deliveryType={deliveryType}
				scheduledDateTime={scheduledDateTime}
			/>
		</Box>
	);
}
